// EditRole.jsx
import {
    ArrowBack as ArrowBackIcon,
    Cancel as CancelIcon,
    Home as HomeIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    FormControlLabel,
    Grid,
    Link,
    Paper,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CustomButton from '../../common/button/commonButton';
import { fetchRoles, resetCurrentRole, updateRole } from '../../redux/role/roleAction';
import { ColoredCheckbox, ModuleChip } from './commonactions';

const EditRole = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { roleId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loading, setLoading] = useState(true);
  const [roleData, setRoleData] = useState(null);

  // Redux state
  const updateRoleState = useSelector((state) => state?.role?.updateRole || {});
  const roleState = useSelector((state) => state?.role?.fetchRoles || {});

  // Available modules
  const modules = [
    { id: 1, name: 'adminDashboard', label: 'Admin Dashboard' },
    { id: 2, name: 'dashboard', label: 'Dashboard' },
    { id: 3, name: 'patient', label: 'Patient Management' },
    { id: 4, name: 'admissions', label: 'Admissions' },
    { id: 5, name: 'doctor', label: 'Doctor Management' },
    { id: 6, name: 'securityOfficer', label: 'Security Officer' },
    { id: 7, name: 'staff', label: 'Staff Management' },
    { id: 8, name: 'block', label: 'Block Management' },
    { id: 9, name: 'floor', label: 'Floor Management' },
    { id: 10, name: 'ward', label: 'Ward Management' },
    { id: 11, name: 'department', label: 'Department' },
    { id: 12, name: 'specialization', label: 'Specialization' },
    { id: 13, name: 'patientTransfer', label: 'Patient Transfer' },
    { id: 14, name: 'adminTransfer', label: 'Admin Transfer' },
    { id: 15, name: 'history', label: 'History' },
    { id: 16, name: 'rolePermission', label: 'Role Permission' },
    { id: 17, name: 'adminProfile', label: 'Admin Profile' },
    { id: 18, name: 'staffProfile', label: 'Staff Profile' },
    { id: 19, name: 'doctorProfile', label: 'Doctor Profile' },
    { id: 20, name: 'staffPatientList', label: 'Staff Patient List' },
    { id: 21, name: 'report', label: 'Reports' },
  ];

  const moduleNameMapping = {
    'RolePerminsions': 'rolePermission',
    'floor': 'floor',
  };

  // Form setup
  const { control, handleSubmit, setValue, getValues, reset, formState: { errors } } = useForm({
    defaultValues: {
      roleName: '',
      modules: {}
    }
  });

  // Initialize module permissions
  const initializeModulePermissions = () => {
    const initialPermissions = {};
    modules.forEach(module => {
      initialPermissions[module.id] = {
        add: false,
        edit: false,
        view: false,
        delete: false,
        allAccess: false,
        noAccess: false,
      };
    });
    return initialPermissions;
  };

  // Fetch role data on component mount
  useEffect(() => {
    dispatch(fetchRoles(1));
  }, [dispatch]);

  // Process fetched role data
  useEffect(() => {
    if (roleState.success && roleState.Roles && roleState.Roles.data) {
      const currentRole = roleState.Roles.data.find(role => role.id.toString() === roleId);
      if (currentRole) {
        setRoleData(currentRole);
        
        // Parse and set permissions
        try {
          const permissions = JSON.parse(currentRole.permissions);
          const formattedPermissions = {};
          
          modules.forEach(module => {
            const normalizedModuleName = moduleNameMapping[module.name] || module.name;
            const modulePermissions = permissions[module.name] || permissions[normalizedModuleName] || {};
            
            formattedPermissions[module.id] = {
              add: modulePermissions.add || false,
              edit: modulePermissions.edit || false,
              view: modulePermissions.view || false,
              delete: modulePermissions.delete || false,
              allAccess: modulePermissions.allaccess || false,
              noAccess: modulePermissions.noaccess || false,
            };
          });
          
          reset({
            roleName: currentRole.name,
            modules: formattedPermissions
          });
          
          setLoading(false);
        } catch (error) {
          console.error('Error parsing role permissions:', error);
          setSnackbarMessage('Error parsing role permissions');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setLoading(false);
        }
      } else {
        setSnackbarMessage('Role not found');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        navigate('/role-permissions');
      }
    }
  }, [roleState, roleId, reset, setValue, navigate]);

  // Handle update role response
  useEffect(() => {
    if (updateRoleState.success) {
      setSnackbarMessage('Role updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      dispatch(resetCurrentRole());
      setTimeout(() => {
        navigate('/role-permissions');
      }, 2000);
    } else if (updateRoleState.error) {
      setSnackbarMessage(`Failed to update role: ${updateRoleState.error}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      dispatch(resetCurrentRole());
    }
  }, [updateRoleState, dispatch, navigate]);

  const handlePermissionChange = (moduleId, permission, value) => {
    const currentValues = getValues(`modules.${moduleId}`);
    const newState = { ...currentValues };
    newState[permission] = value;
    
    if (permission === 'allAccess' && value === true) {
      newState.add = true;
      newState.edit = true;
      newState.view = true;
      newState.delete = true;
      newState.allAccess = true;
      newState.noAccess = false;
    } else if (permission === 'noAccess' && value === true) {
      newState.add = false;
      newState.edit = false;
      newState.view = false;
      newState.delete = false;
      newState.allAccess = false;
      newState.noAccess = true;
    } else if (['add', 'edit', 'view', 'delete'].includes(permission)) {
      newState.allAccess = 
        newState.add && 
        newState.edit && 
        newState.view && 
        newState.delete;
      newState.noAccess = 
        !newState.add && 
        !newState.edit && 
        !newState.view && 
        !newState.delete;
    }
    
    Object.keys(newState).forEach(key => {
      setValue(`modules.${moduleId}.${key}`, newState[key]);
    });
  };

  const onSubmit = (data) => {
    const permissionsObject = {};
    
    modules.forEach(module => {
      const modulePermission = data.modules[module.id] || {};
      let apiModuleName = module.name;
      
      // Convert display names to API names if needed
      if (module.name === 'rolePermission') {
        apiModuleName = 'RolePerminsions';
      }
      
      permissionsObject[apiModuleName] = {
        add: modulePermission.add || false,
        edit: modulePermission.edit || false,
        view: modulePermission.view || false,
        delete: modulePermission.delete || false,
        allaccess: modulePermission.allAccess || false,
        noaccess: modulePermission.noAccess || false
      };
    });
    
    const rolePayload = {
      name: data.roleName,
      status: "1",
      permissions: permissionsObject
    };
    
    dispatch(updateRole(roleId, [rolePayload]));
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    navigate('/role-permissions');
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
      >
        <CircularProgress size={60} sx={{ color: '#13a7b9' }} />
      </Box>
    );
  }

  return (
    <Box p={isMobile ? 2 : 3} sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Header Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          aria-label="breadcrumb" 
          sx={{ mb: 2 }}
          separator="›"
        >
          <Link 
            underline="hover" 
            color="inherit" 
            href="/dashboard"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/role-permissions')}
            sx={{ cursor: 'pointer' }}
          >
            Role Permissions
          </Link>
          <Typography color="text.primary">Edit Role</Typography>
        </Breadcrumbs>

        {/* Page Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            sx={{ color: '#13a7b9' }}
          >
            Back
          </Button>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight="bold" 
            color="#13a7b9"
          >
            Edit Role: {roleData?.name}
          </Typography>
        </Box>
      </Paper>

      {/* Form Section */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Grid container spacing={3}>
            {/* Role Name Field */}
            <Grid item xs={12}>
              <Controller
                name="roleName"
                control={control}
                rules={{ 
                  required: "Role name is required",
                  minLength: {
                    value: 2,
                    message: "Role name must be at least 2 characters"
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Role Name"
                    fullWidth
                    required
                    variant="outlined"
                    error={!!error}
                    helperText={error?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#13a7b9',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#13a7b9',
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>
            
            {/* Module Permissions Section */}
            <Grid item xs={12}>
              <Typography 
                variant="h6" 
                gutterBottom 
                fontWeight="bold" 
                sx={{ 
                  borderBottom: '2px solid #13a7b9', 
                  paddingBottom: 1, 
                  display: 'inline-block',
                  mb: 3
                }}
              >
                Module Permissions
              </Typography>
              
              {modules.map(module => (
                <Paper 
                  key={module.id} 
                  elevation={1} 
                  sx={{ 
                    p: 3, 
                    my: 2,
                    borderRadius: 2,
                    borderLeft: '4px solid',
                    borderColor: (() => {
                      const colors = ['#4CAF50', '#2196F3', '#FF9800', '#F44336', '#673AB7', '#E91E63'];
                      return colors[module.id % colors.length];
                    })(),
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3} md={2}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        <ModuleChip moduleName={module.name} />
                      </Typography>
                    </Grid>
                    
                    {['add', 'edit', 'view', 'delete', 'allAccess', 'noAccess'].map((permission) => {
                      const getPermissionColor = () => {
                        const colors = {
                          'add': '#4CAF50',
                          'edit': '#FF9800',
                          'view': '#2196F3',
                          'delete': '#F44336',
                          'allAccess': '#673AB7',
                          'noAccess': '#9E9E9E'
                        };
                        return colors[permission];
                      };
                      
                      const getPermissionLabel = () => {
                        const labels = {
                          'add': 'Add',
                          'edit': 'Edit',
                          'view': 'View',
                          'delete': 'Delete',
                          'allAccess': 'All Access',
                          'noAccess': 'No Access'
                        };
                        return labels[permission];
                      };
                      
                      return (
                        <Grid item xs={6} sm={2} md={1.5} key={permission}>
                          <Tooltip title={`${getPermissionLabel()} permission for ${module.label}`}>
                            <Controller
                              name={`modules.${module.id}.${permission}`}
                              control={control}
                              render={({ field }) => (
                                <FormControlLabel
                                  control={
                                    <ColoredCheckbox
                                      checked={field.value || false}
                                      onChange={(e) => {
                                        field.onChange(e.target.checked);
                                        handlePermissionChange(module.id, permission, e.target.checked);
                                      }}
                                      color={getPermissionColor()}
                                    />
                                  }
                                  label={
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                                        fontWeight: 500
                                      }}
                                    >
                                      {getPermissionLabel()}
                                    </Typography>
                                  }
                                />
                              )}
                            />
                          </Tooltip>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Grid>
        </Paper>

        {/* Action Buttons */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            mt: 3,
            flexDirection: isMobile ? 'column' : 'row'
          }}
        >
          <CustomButton
            text="Cancel"
            type="cancel"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            fullWidth={isMobile}
          />
          <CustomButton
            text="Update Role"
            type="submit"
            startIcon={<SaveIcon />}
            loading={updateRoleState.loading}
            fullWidth={isMobile}
          />
        </Box>
      </form>
    </Box>
  );
};

export default EditRole;